import { createContext, useCallback, useContext, useMemo } from "react";

import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
  type SearchParser,
} from "./typedRouter";

// The working scope (docs/09 §4, UI-LAW §8): root owns deep-linkable
// `?team=<uuid>` and `?agent=<uuid>` search params. Agent scope is canonical
// with ONLY `?agent=`; the shell resolves the agent's team from `/v1/agents`.
// `?team=` is reserved for All agents / team-bound screens.
export interface WorkingScopeSearch {
  agent?: string;
  team?: string;
}

function readParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function parseWorkingScopeSearch(
  search: Record<string, unknown>,
): WorkingScopeSearch {
  const next: WorkingScopeSearch = {};
  const agent = readParam(search.agent);
  if (agent !== undefined) {
    next.agent = agent;
  }
  const team = readParam(search.team);
  if (team !== undefined) {
    next.team = team;
  }
  return next;
}

export function withWorkingScopeSearch<TSearch extends object>(
  parseRouteSearch: SearchParser<TSearch>,
): SearchParser<TSearch & WorkingScopeSearch> {
  return (search) => ({
    ...parseWorkingScopeSearch(search),
    ...parseRouteSearch(search),
  });
}

export const ResolvedWorkingTeamIdContext = createContext<string | undefined>(
  undefined,
);

export function useResolvedWorkingTeamId(): string | undefined {
  const resolvedTeamId = useContext(ResolvedWorkingTeamIdContext);
  const search = useTypedSearch(parseWorkingScopeSearch);
  return resolvedTeamId ?? readParam(search.team);
}

export function canonicalWorkingScopeSearch(
  search: WorkingScopeSearch,
): WorkingScopeSearch {
  return search.agent !== undefined
    ? { agent: search.agent }
    : { team: search.team };
}

export interface WorkingScope {
  // The active agent id, or undefined for the "All agents" (team-wide) scope.
  agentId?: string;
  // The working team id from the URL, or undefined when the shell resolves it
  // from the fallback chain (agent's team → last-active → first team).
  teamId?: string;
  // Clear the agent back to "All agents" WITHIN the working team.
  clearAgent: (teamId?: string) => void;
  // Scope to an agent. The agent id is enough for a canonical URL; the shell
  // resolves its parent team from the agent detail read.
  selectAgent: (agentId: string, teamId?: string) => void;
  // Switch the working team. Drops the agent scope unless the caller proves
  // the current agent belongs to the new team via `currentAgentTeamId`.
  selectTeam: (teamId: string, currentAgentTeamId?: string) => void;
}

// The working-team fallback chain (docs/09 §4): the scoped agent's team wins
// over any redundant/stale `?team=` param, then `?team=` → persisted
// last-active team → first membership team with agents → first membership
// team. Pure so the chain is testable outside the shell.
export interface WorkingTeamResolutionInput {
  agentTeamId?: string;
  teamIds?: readonly string[];
  teamIdsWithAgents?: ReadonlySet<string>;
  paramTeamId?: string;
  persistedTeamId?: string;
}

export function resolveWorkingTeamId(
  input: WorkingTeamResolutionInput,
): string | undefined {
  return (
    input.agentTeamId ??
    input.paramTeamId ??
    input.persistedTeamId ??
    input.teamIds?.find((id) => input.teamIdsWithAgents?.has(id)) ??
    input.teamIds?.[0]
  );
}

// Resolve the next viable team after a confirmed missing candidate by dropping
// that candidate and reusing the same chain ordering.
export function resolveWorkingTeamIdAfterMissingCandidate(
  input: WorkingTeamResolutionInput,
  missingTeamId: string,
): string | undefined {
  return resolveWorkingTeamId({
    agentTeamId:
      input.agentTeamId === missingTeamId ? undefined : input.agentTeamId,
    teamIds: input.teamIds,
    teamIdsWithAgents: input.teamIdsWithAgents,
    paramTeamId:
      input.paramTeamId === missingTeamId ? undefined : input.paramTeamId,
    persistedTeamId:
      input.persistedTeamId === missingTeamId
        ? undefined
        : input.persistedTeamId,
  });
}

export function useWorkingScope(): WorkingScope {
  const search = useTypedSearch(parseWorkingScopeSearch);
  const navigate = useTypedNavigate<WorkingScopeSearch>();
  const agentId = readParam(search.agent);
  const teamId = readParam(search.team);

  const patchScope = useCallback(
    (patch: WorkingScopeSearch) => {
      // Update ONLY the scope params, staying on the current route (no `to`).
      void navigate({
        search: mergeSearch<WorkingScopeSearch>(patch),
      });
    },
    [navigate],
  );

  const selectAgent = useCallback(
    (nextAgentId: string, nextTeamId?: string) => {
      void nextTeamId;
      // Agent implies team, but the URL does not duplicate the parent.
      patchScope({ agent: nextAgentId, team: undefined });
    },
    [patchScope],
  );

  const selectTeam = useCallback(
    (nextTeamId: string, currentAgentTeamId?: string) => {
      // Team change drops a mismatched agent so deep-link scope and selector
      // scope can never diverge (docs/09 §4).
      const keepAgent = currentAgentTeamId === nextTeamId;
      patchScope(
        keepAgent
          ? { team: undefined }
          : { agent: undefined, team: nextTeamId },
      );
    },
    [patchScope],
  );

  const clearAgent = useCallback(
    (nextTeamId?: string) => {
      // "All agents" stays WITHIN the working team. When the team was resolved
      // from an agent-only URL, callers pass it so the URL becomes team scoped.
      patchScope(
        nextTeamId === undefined
          ? { agent: undefined }
          : { agent: undefined, team: nextTeamId },
      );
    },
    [patchScope],
  );

  return useMemo(
    () => ({ agentId, clearAgent, selectAgent, selectTeam, teamId }),
    [agentId, clearAgent, selectAgent, selectTeam, teamId],
  );
}
